import { MetricTooltip } from '@bb/ui';
import type { AudienceReach, CampaignKpi } from '../../../api/client.ts';
import { definitionHref, formatMetric } from './analytics.ts';

/** Estimated audience of the published revision (BB-44): loading, an answer, or why there is none. */
export type EstimatedAudience = { state: 'loading' } | { state: 'ready'; reach: AudienceReach } | { state: 'everyone' } | { state: 'error'; message: string };

function EstimateTile({ estimate }: { estimate: EstimatedAudience }) {
  const title = 'Estimated audience';
  const formula = 'Profiles that match the audience of the published revision right now, before frequency caps and quiet hours. Large audiences are estimated from a stable sample.';
  if (estimate.state === 'loading') {
    return (
      <li className="kpi-grid__tile">
        <span className="kpi-grid__title">{title}</span>
        <span aria-busy="true">Estimating…</span>
      </li>
    );
  }
  if (estimate.state !== 'ready') {
    return (
      <li className="kpi-grid__tile">
        <span className="kpi-grid__title">{title}</span>
        <span className="hint">{estimate.state === 'everyone' ? 'Everyone in the environment: the campaign has no audience filter.' : `Not available: ${estimate.message}`}</span>
      </li>
    );
  }
  const { reach } = estimate;
  const explanation = reach.mode === 'estimate' ? `Estimated from a sample: ${reach.low.toLocaleString('en-US')}–${reach.high.toLocaleString('en-US')} (95 % interval).` : undefined;
  return (
    <li className="kpi-grid__tile">
      <span className="kpi-grid__title">{title}</span>
      <MetricTooltip title={title} value={reach.count.toLocaleString('en-US')} formula={formula} expression="count(profiles matching the published audience)" {...(explanation ? { explanation } : {})} />
      <span className="hint">{reach.percentOfEnvironment.toFixed(1)} % of the environment</span>
    </li>
  );
}

/**
 * Campaign KPIs (BB-104): each tile's value, formula, count expression and definition come from the same registry
 * that computed it (DEC-017). Rates show what they divide; a rate without a denominator says why it is 0 %.
 */
export function KpiTiles({ kpis, estimate }: { kpis: CampaignKpi[]; estimate: EstimatedAudience }) {
  return (
    <ul aria-label="Key metrics" className="kpi-grid">
      {kpis.map((k) => (
        <li key={k.key} className="kpi-grid__tile">
          <span className="kpi-grid__title">{k.title.en}</span>
          <MetricTooltip title={k.title.en} value={formatMetric(k.format, k.value)} formula={k.formula.en} expression={k.expression} {...(k.explanation ? { explanation: k.explanation.en } : {})} />
          {k.format === 'percent' && k.denominator !== null && (
            <span className="hint">
              {k.numerator.toLocaleString('en-US')} of {k.denominator.toLocaleString('en-US')}
            </span>
          )}
          <a className="kpi-grid__definition" href={definitionHref(k.definitionUrl)} target="_blank" rel="noreferrer" aria-label={`Definition of ${k.title.en}`}>
            Definition
          </a>
        </li>
      ))}
      <EstimateTile estimate={estimate} />
    </ul>
  );
}
